import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Link from "../Link";
import { useTranslation } from "react-i18next";

const InProgressCoinCard = ({
  poolLockSymbol,
  rewardSymbol,
  rewardName,
  rewardDecimals,
  rewardExcerpt,
  rewardIcon,
  rewardSlug,
  estimatedApy,
  rewardTotalAmount,
  earningEndedAt,
  earningStartedAt,
}) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    years: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const isComingSoon = dayjs().isBefore(earningStartedAt);
      const dateCalculated = calculateCountdown(
        isComingSoon ? earningStartedAt : earningEndedAt
      );
      dateCalculated ? setTimeLeft(dateCalculated) : clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [earningStartedAt, earningEndedAt]);

  const calculateCountdown = (date) => {
    let diff =
      (Date.parse(new Date(date).toISOString()) -
        Date.parse(new Date().toISOString())) /
      1000;

    // clear countdown when date is reached
    if (diff <= 0) return false;

    const timeLeft = {
      years: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    // calculate time difference between now and expected date
    const yearToMillisecond = 365.25 * 86400; // 365.25 * 24 * 60 * 60
    if (diff >= yearToMillisecond) {
      timeLeft.years = Math.floor(diff / yearToMillisecond);
      diff -= timeLeft.years * yearToMillisecond;
    }

    // 24 * 60 * 60
    if (diff >= 86400) {
      timeLeft.days = Math.floor(diff / 86400);
      diff -= timeLeft.days * 86400;
    }

    // 60 * 60
    if (diff >= 3600) {
      timeLeft.hours = Math.floor(diff / 3600);
      diff -= timeLeft.hours * 3600;
    }

    if (diff >= 60) {
      timeLeft.minutes = Math.floor(diff / 60);
      diff -= timeLeft.minutes * 60;
    }

    timeLeft.seconds = Math.floor(diff);

    return timeLeft;
  };

  const addLeadingZeros = (value) => {
    let valueToString = String(value);
    while (valueToString.length < 2) {
      valueToString = "0" + value;
    }
    return valueToString;
  };

  const isComingSoon = dayjs().isBefore(earningStartedAt);
  const formatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: rewardDecimals,
  });

  return (
    <div className="coin-card">
      <div className="coin-card__left">
        <div
          className={`coin-card__badge coin-card__badge--${
            isComingSoon ? "comingSoon" : "inProgress"
          }`}
        >
          {isComingSoon
            ? t("zlaunch.inProgress.comingSoon")
            : t("zlaunch.inProgress.earningInprogress")}
        </div>
        <div className="coin-info">
          {rewardIcon ? (
            <img
              className="coin-info__logo"
              src={rewardIcon}
              alt={rewardSymbol}
            ></img>
          ) : (
            <div className="coin-info__logo-alt">
              {rewardSymbol ? rewardSymbol.charAt(0) : ""}
            </div>
          )}
          <span className="coin-info__name">
            {rewardName} ({rewardSymbol})
          </span>
          <span
            className="coin-info__excerpt"
            dangerouslySetInnerHTML={{ __html: rewardExcerpt }}
          ></span>
          <span className="coin-info__earn">
            {t("zlaunch.inProgress.lockSymbolThenEarnSymbol", {
              poolLockSymbol,
              rewardSymbol,
            })}
          </span>
        </div>
      </div>
      <div className="coin-card__right">
        <div className="coin-data">
          {!isComingSoon && (
            <div className="coin-data__line">
              <span className="coin-data__label">
                {t("zlaunch.inProgress.estimatedAPY")}
              </span>
              <span className="coin-data__value-highlighted">
                {Number(estimatedApy).toFixed(2)} %
              </span>
            </div>
          )}
          <div className="coin-data__line">
            <span className="coin-data__label">
              {t("zlaunch.inProgress.totalProjectRewards")}
            </span>
            <span className="coin-data__value">
              {formatter.format(rewardTotalAmount)} {rewardSymbol}
            </span>
          </div>
          {isComingSoon && (
            <div className="coin-data__line">
              <span className="coin-data__label">
                {t("zlaunch.inProgress.earningStartDate")}
              </span>
              <span className="coin-data__value">
                {dayjs(earningStartedAt).format("YYYY-MM-DD HH:mm")}
              </span>
            </div>
          )}
          <div className="coin-data__line">
            <span className="coin-data__label">
              {t("zlaunch.inProgress.earningPeriod")}
            </span>
            <span className="coin-data__value">
              {dayjs(earningEndedAt).diff(dayjs(earningStartedAt), "day")}{" "}
              {t("common.time.days")}
            </span>
          </div>
          <div className="coin-data__line">
            <span className="coin-data__label">
              {isComingSoon
                ? t("zlaunch.inProgress.timeUntilEarningStart")
                : t("zlaunch.inProgress.timeUntilEarningEnds")}
            </span>
          </div>
          <div className="coin-data__line">
            <div className="coin-data__date">
              <span className="coin-data__date__value">
                {addLeadingZeros(timeLeft.days)}
              </span>
              <span className="coin-data__date__label">
                {t("common.time.days")}
              </span>
              <span className="coin-data__date__value">
                {addLeadingZeros(timeLeft.hours)}
              </span>
              <span className="coin-data__date__label">
                {t("common.time.hours")}
              </span>
              <span className="coin-data__date__value">
                {addLeadingZeros(timeLeft.minutes)}
              </span>
              <span className="coin-data__date__label">
                {t("common.time.mins")}
              </span>
            </div>
          </div>
          <Link
            to={`${t("common.exchangeLink")}/earn/zlaunch`}
            rel="noopener noreferrer"
            className="button button-primary coin-card__lock-button mb-2"
            target="_blank"
          >
            {t("zlaunch.inProgress.lockToEarn")}
          </Link>
          <Link
            to={`/coin-info/${rewardSlug}/`}
            className="button button-secondary coin-card__info-button"
          >
            {t("zlaunch.inProgress.learnMore")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InProgressCoinCard;
